import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus } from "lucide-react";
import { useCreateVessel } from "@/hooks/useVessels";
import { VESSEL_KINDS, TANK_SUBTYPES } from "@/lib/vessel-types";

export function CreateVesselDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [vesselKind, setVesselKind] = useState<string>("");
  const [selectedSubtypes, setSelectedSubtypes] = useState<string[]>([]);
  const [capacity, setCapacity] = useState("");
  const [capacityTons, setCapacityTons] = useState("");
  const createVessel = useCreateVessel();

  const isTank = vesselKind === "tank";

  const toggleSubtype = (value: string) => {
    setSelectedSubtypes(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createVessel.mutate(
      {
        name,
        vessel_kind: vesselKind,
        vessel_subtypes: isTank ? selectedSubtypes : [],
        capacity_l: capacity ? parseFloat(capacity) : undefined,
        capacity_tons: capacityTons ? parseFloat(capacityTons) : undefined,
      },
      {
        onSuccess: () => {
          setOpen(false);
          setName("");
          setVesselKind("");
          setSelectedSubtypes([]);
          setCapacity("");
          setCapacityTons("");
        },
      }
    );
  };

  const canSubmit = name && vesselKind && (!isTank || selectedSubtypes.length > 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Vessel
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Vessel</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Vessel Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., B301"
              required
            />
          </div>

          <div>
            <Label>Type *</Label>
            <div className="flex rounded-md border overflow-hidden mt-1.5 text-sm w-fit">
              {VESSEL_KINDS.map((kind, i) => (
                <button
                  key={kind.value}
                  type="button"
                  onClick={() => {
                    setVesselKind(kind.value);
                    setSelectedSubtypes([]);
                  }}
                  className={[
                    "px-4 py-1.5 transition-colors",
                    i > 0 ? "border-l" : "",
                    vesselKind === kind.value
                      ? "bg-muted font-medium text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                >
                  {kind.label}
                </button>
              ))}
            </div>
          </div>

          {isTank && (
            <div>
              <Label>Tank Use *</Label>
              <p className="text-xs text-muted-foreground mb-2">Select all that apply</p>
              <div className="space-y-2">
                {TANK_SUBTYPES.map(subtype => (
                  <div key={subtype.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`subtype-${subtype.value}`}
                      checked={selectedSubtypes.includes(subtype.value)}
                      onCheckedChange={() => toggleSubtype(subtype.value)}
                    />
                    <Label htmlFor={`subtype-${subtype.value}`} className="font-normal cursor-pointer">
                      {subtype.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="capacity">Capacity (Gal)</Label>
              <Input
                id="capacity"
                type="number"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder="e.g., 2900"
                min="0"
                step="0.01"
              />
            </div>
            {isTank && (
              <div>
                <Label htmlFor="capacity-tons">Capacity (Tons)</Label>
                <Input
                  id="capacity-tons"
                  type="number"
                  value={capacityTons}
                  onChange={(e) => setCapacityTons(e.target.value)}
                  placeholder="e.g., 10"
                  min="0"
                  step="0.1"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createVessel.isPending || !canSubmit}>
              {createVessel.isPending ? "Creating..." : "Create Vessel"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
